import { NetworkType, getSupportedNetworkTypeFromChainId } from 'abis/set/sets'
import {
  STAKING_ROUTER_ADDRESS,
  ZERO_EX_AFFILIATE_AVAX_ADDRESS,
  ZERO_EX_AFFILIATE_BASE_ADDRESS,
} from 'app-constants'
import Big from 'big.js'
import { userAccountAtom } from 'context/account'
import type { CurrencyMetadata, ProductMetadata } from 'indices-list'
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai'
import { atomEffect } from 'jotai-effect'
import { RESET, atomWithReset, loadable, useHydrateAtoms } from 'jotai/utils'
import { productMetadataAtom } from 'pages/index-details/storage'
import { ARTXAI, CAI, JMX } from 'products'
import { ProductType } from 'sdk'
import { TransactionStatus } from 'sdk/omni/omni-transaction-service/types'
import {
  createApproveTransactionalAction,
  createV1TransactionalAction,
  trackTransaction,
  transactionsActionsAtom,
  transactionsAtom,
} from 'services'
import atomWithDebounce from 'services/jotai-debounce'
import type { QueryAllowanceDispatch } from 'services/query-v2/allowance/model'
import { approveResponseAtom } from 'services/query-v2/approve'
import {
  assetBalanceQueryAtomsFactory,
  balanceQuery,
  balanceSetterAtom,
  balanceStorageAtom,
} from 'services/query-v2/balances/asset-balance'
import type { QueryBalanceDispatch, QueryBalanceRequest } from 'services/query-v2/balances/model'
import { nativeBalanceQueryAtomsFactory } from 'services/query-v2/balances/native-balance'
import {
  depositPHTRTransactionRequestAtom,
  depositPHTRTransactionResponseAtom,
  redeemPHTRTransactionRequestAtom,
  redeemPHTRTransactionResponseAtom,
} from 'services/query-v2/deposit-redeem-phtr-staking'
import {
  type TransactionRequest,
  type TransactionStatus as TransactionResponse,
  indexV1TransactionRequestAtom,
  indexV1TransactionResponseAtom,
} from 'services/query-v2/deposit-redeem-v1'
import { priceSetterAtom, priceStorageAtom } from 'services/query-v2/prices'
import {
  type PriceRequest,
  type QueryPriceRequest,
  defaultPriceRequest,
} from 'services/query-v2/prices/model'
import {
  indexV2TransactionRequestAtom,
  indexV2TransactionResponseAtom,
} from 'services/query-v2/product-v2'
import { deployments } from 'services/query-v2/product-v2/deployments'
import { permitResponseAtom } from 'services/query-v2/product-v2/permit'
import type { PermitSignStore } from 'services/query-v2/product-v2/permit-utils'
import type { QueryRebalanceStatusDispatch } from 'services/query-v2/rebalance-status/model'
import { amountSetterAtom, buyAmountsStorageAtom } from 'services/query-v2/zero-ex-requests/output'
import {
  type PriceParams,
  type QueryAmountRequest,
  defaultPriceCurrency,
  getInitialPriceParams,
} from 'services/query-v2/zero-ex-requests/output/model'
import {
  type QueryQuotesRequest,
  type QuoteParams,
  defaultQuoteParams,
  getInitialQuotesParams,
} from 'services/query-v2/zero-ex-requests/quotes/model'
import { arbitrumCurrencies, assertMultiply, ethereumCurrencies } from 'utils'
import { InvalidActionError } from 'utils/errors'
import { type Address, isAddressEqual, zeroAddress } from 'viem'
import type { InputChangeInterface } from '../buy/input/storage'
import type { CurrentTransactionStatus, DispatchActions, InvestStorage } from './interface'
import { investStateAtom, transactionConfirm } from './invest-states'
import {
  EPHTRtoPHTROutputQueryAtoms,
  StakedPHTRPriceQueryAtoms,
  assetPriceQueryAtoms,
  indexV1OutputQueryAtoms,
  indexV1PriceQueryAtoms,
  indexV2AnatomyQueryAtoms,
  indexV2OutputQueryAtoms,
  indexV2PriceQueryAtoms,
  quotesQueryAtoms,
  rebalanceStatusQueryAtoms,
  userAllowanceQueryAtoms,
  userContractQueryAtoms,
  userEscrowQueryAtoms,
} from './v1-storage'
import { PHTRPriceQueryAtoms, PHTRToEPHTROutputQueryAtoms } from './v1-storage/index'

const initialDefaultPriceRequestParams = (metadata: ProductMetadata): PriceRequest => {
  return {
    sellCurrency: metadata.acceptedCurrencies[0],
    chainId: getSupportedNetworkTypeFromChainId(metadata.network),
    metadata: metadata,
  }
}

export const inputCurrencyAtom = atom(defaultPriceCurrency)
export const outputCurrencyAtom = atom(defaultPriceCurrency)

export const refreshOutputBalance = atom(false)
export const refreshAllowance = atom(false)

export const input = {
  amount: atomWithReset<Big>(Big(0)), // from deposit input
  value: atom(Big(0)), // deposit dollar value in input
  price: atom(Big(0)), //price for deposit inputToken(selected in selector)
  balance: atom(Big(0)),
  allowance: atom(Big(0)),
  allowanceTaker: atom<Address>(zeroAddress),
}

export const nativeCurrency = {
  asset: atom(defaultPriceCurrency),
  balance: atom(Big(0)),
}

export const v2ExtraAtoms = {
  isRebalance: atom(false),
  isEscrowFilled: atom(false),
  permit: atom<PermitSignStore | undefined>(undefined),
  isUserContract: atom(false),
}

const outputPrice = atom(Big(0))

export const transactionExecution = atom(false)

// Output atoms
const assetOutputRequestAtom = atom<QueryAmountRequest>(indexV2OutputQueryAtoms.response)
const assetOutputRequestParams = atom(indexV2OutputQueryAtoms.params)

const loadableAssetAmountRequestAtom = atom((get) => loadable(get(assetOutputRequestAtom)))

// Taker address
const quotesRequestAtom = atom<QueryQuotesRequest>(quotesQueryAtoms.response)
const quotesRequestParams = atom(quotesQueryAtoms.params)

const loadableQuotesAddressAtom = atom((get) => loadable(get(quotesRequestAtom)))

// Price atoms
const inputPriceRequestAtom = atom<QueryPriceRequest>(assetPriceQueryAtoms.response)
const inputPriceRequestParams = atom(assetPriceQueryAtoms.params)
const loadableInputPriceRequestAtom = atom((get) => loadable(get(inputPriceRequestAtom)))

const outputPriceRequestAtom = atom<QueryPriceRequest>(indexV2PriceQueryAtoms.response)
const outputPriceRequestParams = atom(indexV2PriceQueryAtoms.params)
const loadableOutputPriceRequestAtom = atom((get) => loadable(get(outputPriceRequestAtom)))

// Balances(asset and native) atoms
const assetBalanceQueryAtoms = assetBalanceQueryAtomsFactory()
const nativeBalanceQueryAtoms = nativeBalanceQueryAtomsFactory()

const assetBalanceRequestAtom = atom<QueryBalanceRequest>(assetBalanceQueryAtoms.response)
const assetBalanceDispatchAtom = atom<QueryBalanceDispatch>(assetBalanceQueryAtoms.dispatch)
const nativeBalanceRequestAtom = atom<QueryBalanceRequest>(nativeBalanceQueryAtoms.response)
const nativeBalanceDispatchAtom = atom<QueryBalanceDispatch>(nativeBalanceQueryAtoms.dispatch)

const loadableAssetBalanceRequestAtom = atom((get) => loadable(get(assetBalanceRequestAtom)))
const loadableNativeBalanceRequestAtom = atom((get) => loadable(get(nativeBalanceRequestAtom)))

// Allowance atoms
const allowanceRequestAtom = atom(userAllowanceQueryAtoms.response)
const allowanceDispatchAtom = atom<QueryAllowanceDispatch>(userAllowanceQueryAtoms.dispatch)
const loadableAllowanceRequestAtom = atom((get) => loadable(get(allowanceRequestAtom)))

// Escrow status atoms
const escrowStatusRequestAtom = atom(userEscrowQueryAtoms.response)
const escrowStatusDispatchAtom = atom(userEscrowQueryAtoms.dispatch)
const loadableEscrowStatusRequestAtom = atom((get) => loadable(get(escrowStatusRequestAtom)))

// User is-contract status atoms
const userContractStatusRequestAtom = atom(userContractQueryAtoms.response)
const loadableUserContractStatusRequestAtom = atom((get) =>
  loadable(get(userContractStatusRequestAtom)),
)

//Anatomy atoms
const indexV2AnatomyRequestAtom = atom(indexV2AnatomyQueryAtoms.response)
const loadableIndexV2AnatomyRequestAtom = atom((get) => loadable(get(indexV2AnatomyRequestAtom)))

// Rebalance status atoms
const rebalanceStatusRequestAtom = atom(rebalanceStatusQueryAtoms.response)
const rebalanceStatusDispatchAtom = atom<QueryRebalanceStatusDispatch>(
  rebalanceStatusQueryAtoms.dispatch,
)
const loadableRebalanceStatusRequestAtom = atom((get) => loadable(get(rebalanceStatusRequestAtom)))

// Pending hash status
const pendingTransactionHash = atom<string | undefined>(undefined)

//Debounce atoms
const { isDebouncingAtom, debouncedValueAtom } = atomWithDebounce(input.amount)

const updateDebounce = atomEffect((get, set) => {
  const currentAmount = get(input.amount)
  set(debouncedValueAtom, currentAmount)
})

const isDebouncingAtomAtom = atom((get) => {
  const inputCurrency = get(inputCurrencyAtom)
  const outputCurrency = get(outputCurrencyAtom)
  const isDataLoading = get(get(loadableAssetAmountRequestAtom)).state === 'loading'
  const isDebouncing = get(isDebouncingAtom)
  const currentInputAmount = get(input.amount)
  if (
    isAddressEqual(inputCurrency.address, ARTXAI.address) ||
    isAddressEqual(inputCurrency.address, CAI.address) ||
    isAddressEqual(outputCurrency.address, CAI.address) ||
    isAddressEqual(inputCurrency.address, JMX.address) ||
    isAddressEqual(outputCurrency.address, JMX.address)
  ) {
    //INFO loading if (debounce OR request) running
    return !currentInputAmount.eq(0) && (isDataLoading || isDebouncing)
  }
  //INFO loading if debounce is running
  return !currentInputAmount.eq(0) && isDebouncing
})

//Dispatch actions atoms
const sanitizeChangeAmountAtom = atom(
  null,
  (get, set, payload: InputChangeInterface['payload']) => {
    const { amount } = payload
    const storedAmount = get(input.amount)
    if (amount.length === 0) {
      set(input.amount, Big(0))
    } else if (!storedAmount.eq(amount)) {
      set(input.amount, Big(amount.length !== 0 ? amount : 0))
    }
  },
)

const actionSwitchCurrency = atom(null, (get, set) => {
  const input = get(inputCurrencyAtom)
  const output = get(outputCurrencyAtom)
  set(inputCurrencyAtom, output)
  set(outputCurrencyAtom, input)
})

const actionSelectCurrency = atom(null, (get, set, selectedCurrency: CurrencyMetadata) => {
  const inputIsSell = get(inputCurrencyAtom).address === get(productMetadataAtom).address
  if (inputIsSell) {
    set(outputCurrencyAtom, selectedCurrency)
  } else {
    set(inputCurrencyAtom, selectedCurrency)
  }
})

const actionInputCurrencyChange = atomEffect((get, set) => {
  const input = get(inputCurrencyAtom)
  switch (input.type) {
    case 'token':
    case 'native':
      set(assetPriceQueryAtoms.params, () => ({
        ...defaultPriceRequest,
        sellCurrency: input,
        chainId: input.chainId,
        metadata: get(productMetadataAtom),
      }))
      set(inputPriceRequestParams, () => assetPriceQueryAtoms.params)
      set(inputPriceRequestAtom, () => assetPriceQueryAtoms.response)
      break
    case 'index-v1':
      set(indexV1PriceQueryAtoms.params, () => ({
        ...defaultPriceRequest,
        sellCurrency: input,
        chainId: input.chainId,
        metadata: get(productMetadataAtom),
      }))
      set(inputPriceRequestParams, () => indexV1PriceQueryAtoms.params)
      set(inputPriceRequestAtom, () => indexV1PriceQueryAtoms.response)
      break
    case 'index-v2':
      set(indexV2PriceQueryAtoms.params, () => ({
        ...defaultPriceRequest,
        sellCurrency: input,
        chainId: input.chainId,
        metadata: get(productMetadataAtom),
      }))
      set(inputPriceRequestParams, () => indexV2PriceQueryAtoms.params)
      set(inputPriceRequestAtom, () => indexV2PriceQueryAtoms.response)

      break

    case 'staked-PHTR':
      set(StakedPHTRPriceQueryAtoms.params, () => ({
        ...defaultPriceRequest,
        sellCurrency: input,
        chainId: input.chainId,
        metadata: get(productMetadataAtom),
      }))
      set(inputPriceRequestParams, () => StakedPHTRPriceQueryAtoms.params)
      set(inputPriceRequestAtom, () => StakedPHTRPriceQueryAtoms.response)
      set(userAllowanceQueryAtoms.takerAddress, () => STAKING_ROUTER_ADDRESS)
      break
    case 'PHTR':
      set(PHTRPriceQueryAtoms.params, () => ({
        ...defaultPriceRequest,
        sellCurrency: input,
        chainId: input.chainId,
        metadata: get(productMetadataAtom),
      }))
      set(inputPriceRequestParams, () => PHTRPriceQueryAtoms.params)
      set(inputPriceRequestAtom, () => PHTRPriceQueryAtoms.response)
      set(userAllowanceQueryAtoms.takerAddress, () => STAKING_ROUTER_ADDRESS)
      break
    default:
      break
  }
})

const actionOutputCurrencyChange = atomEffect((get, set) => {
  const output = get(outputCurrencyAtom)
  switch (output.type) {
    case 'token':
    case 'native':
      set(assetPriceQueryAtoms.params, () => ({
        ...defaultPriceRequest,
        sellCurrency: output,
        chainId: output.chainId,
        metadata: get(productMetadataAtom),
      }))
      set(outputPriceRequestParams, () => assetPriceQueryAtoms.params)
      set(outputPriceRequestAtom, () => assetPriceQueryAtoms.response)
      break

    case 'index-v1':
      set(indexV1PriceQueryAtoms.params, () => ({
        ...defaultPriceRequest,
        sellCurrency: output,
        chainId: output.chainId,
        metadata: get(productMetadataAtom),
      }))
      set(outputPriceRequestParams, () => indexV1PriceQueryAtoms.params)
      set(outputPriceRequestAtom, () => indexV1PriceQueryAtoms.response)

      break

    case 'index-v2':
      set(indexV2PriceQueryAtoms.params, () => ({
        ...defaultPriceRequest,
        sellCurrency: output,
        chainId: output.chainId,
        metadata: get(productMetadataAtom),
      }))
      set(outputPriceRequestParams, () => indexV2PriceQueryAtoms.params)
      set(outputPriceRequestAtom, () => indexV2PriceQueryAtoms.response)
      set(
        userAllowanceQueryAtoms.takerAddress,
        () => deployments[output.chainId][output.address].Messenger,
      )

      break

    case 'PHTR':
      set(PHTRPriceQueryAtoms.params, () => ({
        ...defaultPriceRequest,
        sellCurrency: output,
        chainId: output.chainId,
        metadata: get(productMetadataAtom),
      }))
      set(outputPriceRequestParams, () => PHTRPriceQueryAtoms.params)
      set(outputPriceRequestAtom, () => PHTRPriceQueryAtoms.response)
      set(userAllowanceQueryAtoms.takerAddress, () => STAKING_ROUTER_ADDRESS)
      break
    case 'staked-PHTR':
      set(StakedPHTRPriceQueryAtoms.params, () => ({
        ...defaultPriceRequest,
        sellCurrency: output,
        chainId: output.chainId,
        metadata: get(productMetadataAtom),
      }))
      set(outputPriceRequestParams, () => StakedPHTRPriceQueryAtoms.params)
      set(outputPriceRequestAtom, () => StakedPHTRPriceQueryAtoms.response)
      set(userAllowanceQueryAtoms.takerAddress, () => STAKING_ROUTER_ADDRESS)
      break

    default:
      break
  }
})

export const executionAtom = atom<TransactionRequest>(indexV1TransactionRequestAtom)
const transactionResponsesAtom = atom<TransactionResponse>(indexV1TransactionResponseAtom)
const lastSyncedTransactionAtom = atom<string | undefined>(undefined)

const dispatchAtom = atom(null, (_, set, action: DispatchActions) => {
  switch (action.type) {
    case 'asset-select':
      set(actionSelectCurrency, action.payload.asset)
      break
    case 'input-change':
      set(sanitizeChangeAmountAtom, action.payload)
      break
    case 'switch-mode':
      set(actionSwitchCurrency)
      break
    case 'transaction-confirm':
      if (action.payload.type === 'success') set(transactionConfirm, action.payload)
      break
    case 'refresh-allowance':
      set(refreshAllowance, true)
      break
    default:
      throw new InvalidActionError(action)
  }
})

// Approval transaction status
const loadableApproveResponse = loadable(approveResponseAtom)

const updateApproveResponse = atomEffect((get, set) => {
  const approveResponse = get(loadableApproveResponse)
  const transactions = get(transactionsAtom)

  if (approveResponse.state === 'hasData') {
    if (transactions.findIndex((tx) => tx.hash === approveResponse.data.hash) === -1) {
      const action = createApproveTransactionalAction({
        action: 'approve',
        token: approveResponse.data.params.inputCurrency,
        amount: approveResponse.data.params.sellAmount,
      })

      const transaction = trackTransaction(
        approveResponse.data.params.account,
        approveResponse.data.hash,
        action,
        approveResponse.data.params.inputCurrency.chainId,
        approveResponse.data.params.isUserContract,
        true,
      )
      set(transactionsActionsAtom, { type: 'add', payload: transaction })
      set(pendingTransactionHash, approveResponse.data.hash)
    }
  }
})

const loadablePermitResponse = loadable(permitResponseAtom)

const updatePermitResponse = atomEffect((get, set) => {
  const permitResponse = get(loadablePermitResponse)

  if (permitResponse.state === 'hasData') {
    set(v2ExtraAtoms.permit, permitResponse.data)
  }
})

const updateDepositResponse = atomEffect((get, set) => {
  const transactionResponse = get(loadable(get(transactionResponsesAtom)))
  const transactions = get(transactionsAtom)
  const lastSyncedTransaction = get(lastSyncedTransactionAtom)

  if (
    transactionResponse &&
    transactionResponse.state === 'hasData' &&
    transactionResponse.data !== undefined
  ) {
    if (
      lastSyncedTransaction === undefined ||
      transactionResponse.data.hash !== lastSyncedTransaction
    ) {
      const inputCurrency = get(inputCurrencyAtom)
      const metadata = get(productMetadataAtom)
      const isSellTransaction = inputCurrency.address === metadata.address
      const isRetry =
        (transactionResponse.data.action && transactionResponse.data.action === 'retry') ?? false
      const actionText = isRetry ? 'retry' : !isSellTransaction ? 'buy' : 'sell'

      const actionAmount = isSellTransaction
        ? transactionResponse.data.params.sellAmount
        : transactionResponse.data.buyAmount

      if (
        transactions.findIndex(
          (tx) => transactionResponse.data && tx.hash === transactionResponse.data.hash,
        ) === -1
      ) {
        const action = createV1TransactionalAction({
          action: actionText,
          amount: actionAmount,
          inputToken: transactionResponse.data.params.inputCurrency,
          outputToken: transactionResponse.data.params.outputCurrency,
          productAddress: metadata.address,
        })

        const transaction = trackTransaction(
          transactionResponse.data.params.account,
          transactionResponse.data.hash,
          action,
          transactionResponse.data.params.inputCurrency.chainId,
          transactionResponse.data.isUserContract,
          transactionResponse.data.isSingleChain,
          transactionResponse.data.chains,
        )
        set(transactionsActionsAtom, { type: 'add', payload: transaction })
        set(pendingTransactionHash, transactionResponse.data.hash)
        set(v2ExtraAtoms.permit, undefined)
        set(lastSyncedTransactionAtom, transactionResponse.data.hash)
      }
    }
  }
})

const replacePendingHash = atomEffect((get, set) => {
  const hash = get(pendingTransactionHash)
  if (hash !== undefined) {
    const transactions = get(transactionsAtom)
    const transaction = transactions.find((tx) => tx.hash === hash)
    if (
      transactions.length !== 0 &&
      transaction !== undefined &&
      transaction.replacementHash !== undefined
    ) {
      set(pendingTransactionHash, transaction.replacementHash)
    }
  }
})

const pendingTransactionStatusAtom = atom<CurrentTransactionStatus>((get) => {
  const hash = get(pendingTransactionHash)
  if (hash !== undefined) {
    const transactions = get(transactionsAtom)
    const transaction = transactions.find((tx) => tx.hash === hash)
    if (transactions.length === 0 || transaction === undefined) return undefined
    return transaction.status
  }
  return undefined
})

// Buy amount update atoms
const updateParams = atomEffect((get, set) => {
  const inputCurrency = get(inputCurrencyAtom)
  const outputCurrency = get(outputCurrencyAtom)
  const metadata = get(productMetadataAtom)
  const permit = get(v2ExtraAtoms.permit)
  const value = get(inputValueAtom)
  const currentAnatomy = get(userEscrowQueryAtoms.indexAnatomy)

  const indexAddress =
    inputCurrency.type === 'index-v2' ? inputCurrency.address : outputCurrency.address

  const params: PriceParams = {
    ...defaultQuoteParams,
    sellValue: value,
    sellCurrency: inputCurrency,
    buyCurrency: outputCurrency,
    slippagePercentage: metadata.defaultSlippagePercentage, // TODO: read from slippage percentage atom
    chainId: metadata.network,
    productAddress: metadata.address,
    userAddress: get(userAccountAtom) ?? zeroAddress,
  }

  const quoteParams = {
    ...defaultQuoteParams,
    sellAmount: value,
    sellCurrency: inputCurrency,
    buyCurrency: outputCurrency,
    enableSlippageProtection: true,
    slippagePercentage: metadata.defaultSlippagePercentage, // TODO: read from slippage percentage atom
    chainId: metadata.network,
    productAddress: metadata.address,
    account: get(userAccountAtom) ?? zeroAddress,
    affiliateAddress:
      metadata.network === NetworkType.Avalanche
        ? ZERO_EX_AFFILIATE_AVAX_ADDRESS
        : metadata.network === NetworkType.Base
          ? ZERO_EX_AFFILIATE_BASE_ADDRESS
          : zeroAddress,
  }

  const pair = `${inputCurrency.type}/${outputCurrency.type}`

  if (permit?.deadline) {
    const sellAmount = get(debouncedValueAtom)
    const now = Math.floor(new Date().getTime() / 1000)
    if (
      permit.deadline < BigInt(now) ||
      permit.address !== inputCurrency.address ||
      !permit.amount.eq(sellAmount)
    ) {
      set(v2ExtraAtoms.permit, undefined)
    }
  }

  switch (pair) {
    case 'index-v1/token':
    case 'index-v1/native':
    case 'token/index-v1':
    case 'native/index-v1':
      set(indexV1OutputQueryAtoms.params, () => params)
      set(assetOutputRequestParams, () => indexV1OutputQueryAtoms.params)
      set(assetOutputRequestAtom, () => indexV1OutputQueryAtoms.response)
      set(executionAtom, indexV1TransactionRequestAtom)
      set(transactionResponsesAtom, indexV1TransactionResponseAtom)

      set(quotesQueryAtoms.params, () => quoteParams)
      set(quotesRequestParams, () => quotesQueryAtoms.params)
      break
    case 'index-v2/token':
    case 'index-v2/native':
    case 'token/index-v2':
    case 'native/index-v2':
      set(indexV2OutputQueryAtoms.params, () => params)
      currentAnatomy.productAddress !== zeroAddress &&
        set(indexV2OutputQueryAtoms.indexAnatomy, () => currentAnatomy)
      set(assetOutputRequestParams, () => indexV2OutputQueryAtoms.params)
      set(assetOutputRequestAtom, () => indexV2OutputQueryAtoms.response)
      set(executionAtom, indexV2TransactionRequestAtom)
      set(transactionResponsesAtom, indexV2TransactionResponseAtom)
      set(indexV2AnatomyQueryAtoms.indexAddress, indexAddress)
      set(rebalanceStatusQueryAtoms.indexAddress, indexAddress)
      //
      set(quotesQueryAtoms.params, () => quoteParams)
      set(quotesRequestParams, () => quotesQueryAtoms.params)
      break
    case 'PHTR/staked-PHTR':
      set(PHTRToEPHTROutputQueryAtoms.params, () => params)
      set(assetOutputRequestParams, () => PHTRToEPHTROutputQueryAtoms.params)
      set(assetOutputRequestAtom, () => PHTRToEPHTROutputQueryAtoms.response)
      set(executionAtom, depositPHTRTransactionRequestAtom)
      set(transactionResponsesAtom, depositPHTRTransactionResponseAtom)
      break
    case 'staked-PHTR/PHTR':
      set(EPHTRtoPHTROutputQueryAtoms.params, () => params)
      set(assetOutputRequestParams, () => EPHTRtoPHTROutputQueryAtoms.params)
      set(assetOutputRequestAtom, () => EPHTRtoPHTROutputQueryAtoms.response)
      set(executionAtom, redeemPHTRTransactionRequestAtom)
      set(transactionResponsesAtom, redeemPHTRTransactionResponseAtom)
      break

    default:
      break
  }
})

const updateAmountParams = atomEffect((get, set) => {
  const debouncedValue = get(debouncedValueAtom)
  const value = get(inputValueAtom)
  const currentParams = get(get(assetOutputRequestParams))
  const currentAnatomy = get(userEscrowQueryAtoms.indexAnatomy)
  const { sellCurrency: input, buyCurrency: output } = currentParams

  const params: PriceParams = {
    ...currentParams,
    sellAmount: debouncedValue,
    sellValue: value,
  }

  const pair = `${input.type}/${output.type}`

  switch (pair) {
    case 'index-v1/token':
    case 'index-v1/native':
    case 'token/index-v1':
    case 'native/index-v1':
      set(indexV1OutputQueryAtoms.params, () => params)
      set(assetOutputRequestParams, () => indexV1OutputQueryAtoms.params)
      // TODO: check do we need here to set assetOutputRequestParams atom
      break

    case 'index-v2/token':
    case 'index-v2/native':
    case 'token/index-v2':
    case 'native/index-v2':
      set(indexV2OutputQueryAtoms.params, () => params)
      currentAnatomy.productAddress !== zeroAddress &&
        set(indexV2OutputQueryAtoms.indexAnatomy, () => currentAnatomy)

      // TODO: check do we need here to set assetOutputRequestParams atom
      break
    case 'PHTR/staked-PHTR':
      set(PHTRToEPHTROutputQueryAtoms.params, () => params)
      set(assetOutputRequestParams, () => PHTRToEPHTROutputQueryAtoms.params)
      break
    case 'staked-PHTR/PHTR':
      set(EPHTRtoPHTROutputQueryAtoms.params, () => params)
      set(assetOutputRequestParams, () => EPHTRtoPHTROutputQueryAtoms.params)
      break

    default:
      break
  }
})

const updateBuyAmount = atomEffect((get, set) => {
  const updatedParams = get(get(assetOutputRequestParams))

  if (
    isAddressEqual(updatedParams.sellCurrency.address, ARTXAI.address) ||
    isAddressEqual(updatedParams.productAddress, CAI.address) ||
    isAddressEqual(updatedParams.productAddress, JMX.address)
  ) {
    const buyAmount = get(get(loadableAssetAmountRequestAtom))
    if (buyAmount.state === 'hasData') {
      if (updatedParams.sellAmount.gt(0)) {
        set(amountSetterAtom, buyAmount.data)
      }
    }
  } else {
    const price = get(outputPrice)

    if (price.gt(0) && updatedParams.sellValue) {
      if (updatedParams.sellAmount.gt(0)) {
        set(amountSetterAtom, {
          chainId: updatedParams.chainId,
          currency: updatedParams.buyCurrency,
          buyAmount: updatedParams.sellValue.div(price),
        })
      }
    }
  }
})

// Prices update atoms
const updateBuyPrice = atomEffect((get, set) => {
  const price = get(get(loadableInputPriceRequestAtom))
  if (price.state === 'hasData') {
    set(priceSetterAtom, price.data)
  }
})

const updateSellPrice = atomEffect((get, set) => {
  const price = get(get(loadableOutputPriceRequestAtom))
  if (price.state === 'hasData') {
    set(priceSetterAtom, price.data)
  }
})

// Input prices update atoms
const updateInputPrice = atomEffect((get, set) => {
  const inputCurrency = get(inputCurrencyAtom)
  const prices = get(priceStorageAtom)
  const price = prices[inputCurrency.chainId][inputCurrency.address]

  if (price !== undefined) {
    return set(input.price, price)
  }
})

// Output prices update atoms
const updateOutputPrice = atomEffect((get, set) => {
  const outputCurrency = get(outputCurrencyAtom)
  const prices = get(priceStorageAtom)
  const price = prices[outputCurrency.chainId][outputCurrency.address]

  if (price !== undefined) {
    return set(outputPrice, price)
  }
})

// Input & Output values atoms
const inputValueAtom = atom((get) => {
  const inputAmount = get(input.amount)
  const price = get(input.price)
  assertMultiply(inputAmount, price)
  return inputAmount.mul(price)
})

const outputValueAtom = atom((get) => {
  const outputAmount = get(outputAmountAtom)
  const price = get(outputPrice)
  if (outputAmount !== undefined) {
    assertMultiply(outputAmount, price)
    return outputAmount.mul(price)
  }
  return undefined
})

export const outputAmountAtom = atom((get) => {
  const currentInputAmount = get(input.amount)
  const inputCurrency = get(inputCurrencyAtom)
  const multiplier =
    inputCurrency.type === 'index-v2' ? 0.97 : inputCurrency.type === 'native' ? 0.999 : 0.998
  const outputCurrency = get(outputCurrencyAtom)
  const buyAmounts = get(buyAmountsStorageAtom)
  let amount = Big(0)
  if (
    isAddressEqual(inputCurrency.address, ARTXAI.address) ||
    isAddressEqual(inputCurrency.address, CAI.address) ||
    isAddressEqual(outputCurrency.address, CAI.address) ||
    isAddressEqual(inputCurrency.address, JMX.address) ||
    isAddressEqual(outputCurrency.address, JMX.address)
  ) {
    //INFO: real quoting
    amount = buyAmounts?.[inputCurrency.chainId]?.[inputCurrency.address]
  } else {
    //INFO: fake quoting
    amount = buyAmounts?.[outputCurrency.chainId]?.[outputCurrency.address]
  }

  if (currentInputAmount.eq(0)) return Big(0)
  if (amount !== undefined) {
    assertMultiply(amount, multiplier)
    if (
      isAddressEqual(inputCurrency.address, ARTXAI.address) ||
      isAddressEqual(inputCurrency.address, CAI.address) ||
      isAddressEqual(outputCurrency.address, CAI.address) ||
      isAddressEqual(inputCurrency.address, JMX.address) ||
      isAddressEqual(outputCurrency.address, JMX.address)
    ) {
      //INFO: real amount
      return amount
    }
    //INFO: fake amount
    return amount.mul(multiplier)
  }
  return undefined
})

const updateBalancesStorage = atomEffect((get, set) => {
  const inputCurrency = get(inputCurrencyAtom)
  const currentUser = get(userAccountAtom)
  const balance = get(get(loadableAssetBalanceRequestAtom))
  if (currentUser) {
    if (balance.state === 'hasData') {
      set(balanceSetterAtom, {
        asset: inputCurrency,
        userAddress: currentUser ?? zeroAddress,
        amount: balance.data,
        chainId: inputCurrency.chainId,
      })
    }
  }
})

const updateCurrencyAllowanceParams = atomEffect((get, set) => {
  const inputCurrency = get(inputCurrencyAtom)
  const currentUser = get(userAccountAtom)

  set(userAllowanceQueryAtoms.currentAsset, inputCurrency)
  set(userAllowanceQueryAtoms.userAddress, currentUser ?? zeroAddress)
})

const updatingOutputBalanceAtom = atom(null, async (get, set) => {
  const outputCurrency = get(outputCurrencyAtom)
  if (outputCurrency.type === 'index-v2') {
    const currentUser = get(userAccountAtom)
    if (currentUser && currentUser !== zeroAddress) {
      const outputBalance = await balanceQuery(currentUser, outputCurrency)
      set(balanceSetterAtom, {
        asset: outputCurrency,
        userAddress: currentUser,
        amount: outputBalance,
        chainId: outputCurrency.chainId,
      })
    }
  }
})

const forceAllowanceUpdate = atomEffect((get, set) => {
  const pendingTransactionStatus = get(pendingTransactionStatusAtom)
  const refreshAllowanceStatus = get(refreshAllowance)
  const isRefetchNeeded =
    pendingTransactionStatus === TransactionStatus.DELIVERED || refreshAllowanceStatus
  const shouldClearTransactionHash =
    pendingTransactionStatus === TransactionStatus.DELIVERED ||
    pendingTransactionStatus === TransactionStatus.FAILED

  if (isRefetchNeeded) {
    const allowanceStatus = get(get(allowanceDispatchAtom))
    const escrowState = get(get(escrowStatusDispatchAtom))
    const assetBalanceStatus = get(get(assetBalanceDispatchAtom))
    const nativeBalanceStatus = get(get(nativeBalanceDispatchAtom))

    allowanceStatus
      .refetch()
      .then(() => assetBalanceStatus.refetch())
      .then(() => nativeBalanceStatus.refetch())
      .then(() => escrowState.refetch())
  }

  if (shouldClearTransactionHash) {
    set(updatingOutputBalanceAtom)
    set(pendingTransactionHash, undefined)
  }
  if (refreshAllowanceStatus) {
    set(refreshAllowance, false)
  }
})

const updateAllowanceTakerAddress = atomEffect((get, set) => {
  const currentUser = get(userAccountAtom)
  const loadableQuotes = get(get(loadableQuotesAddressAtom))
  if (currentUser && loadableQuotes.state === 'hasData' && loadableQuotes.data.to) {
    set(userAllowanceQueryAtoms.takerAddress, loadableQuotes.data.to)
  } else {
    set(userAllowanceQueryAtoms.takerAddress, '0x0000000000005E88410CcDFaDe4a5EfaE4b49562')
  }
})

const updateAllowance = atomEffect((get, set) => {
  const currentUser = get(userAccountAtom)
  const allowance = get(get(loadableAllowanceRequestAtom))

  if (currentUser) {
    if (allowance.state === 'hasData') {
      set(input.allowance, allowance.data.value)
      set(input.allowanceTaker, allowance.data.taker)
    }
  }
})

const updateEscrowStatus = atomEffect((get, set) => {
  const currentUser = get(userAccountAtom)
  const escrowState = get(get(loadableEscrowStatusRequestAtom))
  if (currentUser) {
    if (escrowState.state === 'hasData') {
      set(v2ExtraAtoms.isEscrowFilled, !escrowState.data.isAllowedAmount)
    }
  }
})

const updateUserContractStatus = atomEffect((get, set) => {
  const currentUser = get(userAccountAtom)
  const isUserContractState = get(get(loadableUserContractStatusRequestAtom))
  if (currentUser) {
    if (isUserContractState.state === 'hasData') {
      set(v2ExtraAtoms.isUserContract, isUserContractState.data)
    }
  }
})

const updateIndexAnatomy = atomEffect((get, set) => {
  const anatomy = get(get(loadableIndexV2AnatomyRequestAtom))
  if (anatomy.state === 'hasError') {
    throw anatomy.error
  }

  if (anatomy.state === 'hasData') {
    set(userEscrowQueryAtoms.indexAnatomy, anatomy.data)
  }
})

const updateRebalanceStatus = atomEffect((get, set) => {
  const rebalanceStatus = get(get(loadableRebalanceStatusRequestAtom))

  if (rebalanceStatus.state === 'hasData') {
    set(v2ExtraAtoms.isRebalance, rebalanceStatus.data)
  }
})

const updateNativeBalance = atomEffect((get, set) => {
  const nativeBalance = get(get(loadableNativeBalanceRequestAtom))
  if (nativeBalance.state === 'hasData') {
    set(nativeCurrency.balance, nativeBalance.data)
  }
})

const updateCurrencyBalanceParams = atomEffect((get, set) => {
  const inputCurrency = get(inputCurrencyAtom)
  const currentUser = get(userAccountAtom)

  set(assetBalanceQueryAtoms.currentAsset, inputCurrency)
  set(assetBalanceQueryAtoms.userAddress, currentUser ?? zeroAddress)
})

const updateCurrentInputBalance = atomEffect((get, set) => {
  const currentUser = get(userAccountAtom)
  const inputCurrency = get(inputCurrencyAtom)
  const allBalances = get(balanceStorageAtom)
  if (
    currentUser &&
    allBalances &&
    inputCurrency &&
    allBalances[currentUser][inputCurrency.chainId][inputCurrency.address]
  ) {
    set(input.balance, allBalances[currentUser][inputCurrency.chainId][inputCurrency.address])
  } else {
    set(input.balance, Big(0))
  }
})

const updateRetryAmount = atomEffect((get, set) => {
  const isEscrowFilled = get(v2ExtraAtoms.isEscrowFilled)
  const transactions = get(transactionsAtom)
  const omniTxs = transactions.filter(
    (tx) => tx.action.action === 'sell' && tx.additionalChains !== undefined,
  )
  if (isEscrowFilled && omniTxs.length !== 0) {
    set(input.amount, Big(omniTxs[omniTxs.length - 1].action.indexDetails?.value ?? 0))
  }
})

export const createAtomicInvestStorage = (productMetadata: ProductMetadata): InvestStorage => {
  const acceptedCurrency = productMetadata.acceptedCurrencies[0]
  const initialOutputParams: PriceParams = getInitialPriceParams(productMetadata, acceptedCurrency)

  const initialDefaultOutputRequestParams: PriceRequest = {
    sellCurrency: productMetadata.productCurrency,
    chainId: getSupportedNetworkTypeFromChainId(productMetadata.network),
    metadata: productMetadata,
  }
  const userAddress = useAtomValue(userAccountAtom)

  const initialQuoteParams: QuoteParams = getInitialQuotesParams(
    productMetadata,
    acceptedCurrency,
    userAddress ? userAddress : zeroAddress,
  )

  const setUserAddressForNativeBalance = useSetAtom(nativeBalanceQueryAtoms.userAddress)
  setUserAddressForNativeBalance(userAddress ? userAddress : zeroAddress)

  const setUserAddressForAssetBalance = useSetAtom(assetBalanceQueryAtoms.userAddress)
  setUserAddressForAssetBalance(userAddress ? userAddress : zeroAddress)

  const setUserAddressForAllowance = useSetAtom(userAllowanceQueryAtoms.userAddress)
  setUserAddressForAllowance(userAddress ? userAddress : zeroAddress)

  const setUserAddressEscrowStatus = useSetAtom(userEscrowQueryAtoms.userAddress)
  setUserAddressEscrowStatus(userAddress ? userAddress : zeroAddress)

  const setUserAddressContractStatus = useSetAtom(userContractQueryAtoms.userAddress)
  setUserAddressContractStatus(userAddress ? userAddress : zeroAddress)

  switch (productMetadata.productType) {
    case ProductType.INDEX_V1: {
      useHydrateAtoms([
        //extra
        // [nativeBalanceQueryAtoms.userAddress, userAddress],
        // [assetBalanceQueryAtoms.userAddress, userAddress],
        // [userAllowanceQueryAtoms.userAddress, userAddress],
        // [userEscrowQueryAtoms.userAddress, userAddress],
        // [userContractQueryAtoms.userAddress, userAddress],
        // TODO:

        // Input
        [input.amount, RESET],
        [inputCurrencyAtom, acceptedCurrency],
        [outputCurrencyAtom, productMetadata.productCurrency],

        //Native
        [nativeCurrency.asset, acceptedCurrency],
        [nativeBalanceQueryAtoms.currentAsset, acceptedCurrency],
        [nativeBalanceRequestAtom, nativeBalanceQueryAtoms.response],

        // Asset Output
        [indexV1OutputQueryAtoms.params, initialOutputParams],
        [assetOutputRequestParams, indexV1OutputQueryAtoms.params],
        [assetOutputRequestAtom, indexV1OutputQueryAtoms.response],

        //Quotes
        [quotesQueryAtoms.params, initialQuoteParams],
        [quotesRequestParams, quotesQueryAtoms.params],
        [quotesRequestAtom, quotesQueryAtoms.response],

        // Asset Price
        [assetPriceQueryAtoms.params, initialDefaultPriceRequestParams(productMetadata)],
        [indexV1PriceQueryAtoms.params, initialDefaultOutputRequestParams],

        [inputPriceRequestAtom, assetPriceQueryAtoms.response],
        [inputPriceRequestParams, assetPriceQueryAtoms.params],
        [outputPriceRequestAtom, indexV1PriceQueryAtoms.response],
        [outputPriceRequestParams, indexV1PriceQueryAtoms.params],

        //Allowance
        [userAllowanceQueryAtoms.currentAsset, acceptedCurrency],
        [allowanceRequestAtom, userAllowanceQueryAtoms.response],
        [allowanceDispatchAtom, userAllowanceQueryAtoms.dispatch],

        // Asset Balance
        [assetBalanceQueryAtoms.currentAsset, acceptedCurrency],
        [assetBalanceRequestAtom, assetBalanceQueryAtoms.response],
      ])
      break
    }
    case ProductType.INDEX_V2:
      useHydrateAtoms([
        [input.amount, RESET],
        [inputCurrencyAtom, acceptedCurrency],
        [outputCurrencyAtom, productMetadata.productCurrency],

        [nativeCurrency.asset, arbitrumCurrencies.ETH],
        [nativeBalanceQueryAtoms.currentAsset, arbitrumCurrencies.ETH],
        [nativeBalanceRequestAtom, nativeBalanceQueryAtoms.response],

        // // Asset Balance
        // [assetBalanceQueryAtoms.currentAsset, acceptedCurrency],
        // [assetBalanceRequestAtom, assetBalanceQueryAtoms.request],

        //Escrow status
        // [escrowStatusRequestAtom, userEscrowQueryAtoms.request],
        // [escrowStatusDispatchAtom, userEscrowQueryAtoms.status],

        //Rebalance status
        [rebalanceStatusRequestAtom, rebalanceStatusQueryAtoms.response],
        [rebalanceStatusDispatchAtom, rebalanceStatusQueryAtoms.dispatch],

        // Asset Output
        [indexV2OutputQueryAtoms.params, initialOutputParams],
        [assetOutputRequestParams, indexV2OutputQueryAtoms.params],
        [assetOutputRequestAtom, indexV2OutputQueryAtoms.response],

        //Quotes
        [quotesQueryAtoms.params, initialQuoteParams],
        [quotesRequestParams, quotesQueryAtoms.params],
        [quotesRequestAtom, quotesQueryAtoms.response],

        // Asset Price
        [assetPriceQueryAtoms.params, initialDefaultPriceRequestParams(productMetadata)],
        [indexV2PriceQueryAtoms.params, initialDefaultOutputRequestParams],

        [inputPriceRequestAtom, assetPriceQueryAtoms.response],
        [inputPriceRequestParams, assetPriceQueryAtoms.params],
        [outputPriceRequestAtom, indexV2PriceQueryAtoms.response],
        [outputPriceRequestParams, indexV2PriceQueryAtoms.params],
      ])
      break

    case ProductType.STAKING:
      useHydrateAtoms([
        [input.amount, RESET],
        // Input
        [inputCurrencyAtom, acceptedCurrency],
        [outputCurrencyAtom, productMetadata.productCurrency],

        //Native
        [nativeCurrency.asset, ethereumCurrencies.ETH],
        [nativeBalanceQueryAtoms.currentAsset, ethereumCurrencies.ETH],
        [nativeBalanceRequestAtom, nativeBalanceQueryAtoms.response],

        // Product Price
        [PHTRPriceQueryAtoms.params, initialDefaultOutputRequestParams],
        [StakedPHTRPriceQueryAtoms.params, initialDefaultOutputRequestParams],

        //staked PHTR output
        [PHTRToEPHTROutputQueryAtoms.params, initialOutputParams],
        [assetOutputRequestParams, PHTRToEPHTROutputQueryAtoms.params],
        [assetOutputRequestAtom, PHTRToEPHTROutputQueryAtoms.response],

        //Quotes
        [quotesQueryAtoms.params, initialQuoteParams],
        [quotesRequestParams, quotesQueryAtoms.params],
        [quotesRequestAtom, quotesQueryAtoms.response],

        //Allowance
        [userAllowanceQueryAtoms.currentAsset, acceptedCurrency],
        [allowanceRequestAtom, userAllowanceQueryAtoms.response],
        [allowanceDispatchAtom, userAllowanceQueryAtoms.dispatch],
      ])
      break
    // TODO: add hydration case for v2 product
  }

  const storage: InvestStorage = {
    input: {
      amount: useAtomValue(input.amount),
      asset: useAtomValue(inputCurrencyAtom),
      value: useAtomValue(inputValueAtom),
      price: useAtomValue(input.price),
      balance: useAtomValue(input.balance),
      allowance: useAtomValue(input.allowance),
      adjustedAmount: useAtomValue(input.balance),
    },
    output: {
      amount: useAtomValue(outputAmountAtom),
      asset: useAtomValue(outputCurrencyAtom),
      value: useAtomValue(outputValueAtom),
      price: useAtomValue(outputPrice),
    },
    nativeCurrency: {
      asset: useAtomValue(nativeCurrency.asset),
      balance: useAtomValue(nativeCurrency.balance),
    },
    investState: useAtomValue(investStateAtom),
    isDebounce: useAtomValue(isDebouncingAtomAtom),
    pendingTransactionStatus: useAtomValue(pendingTransactionStatusAtom),
    dispatch: useSetAtom(dispatchAtom),
  }

  useAtom(updateDebounce)
  useAtom(updateParams)
  useAtom(updateAmountParams)
  useAtom(updateBuyAmount)
  useAtom(updateBuyPrice)
  useAtom(updateSellPrice)

  useAtom(updateInputPrice)
  useAtom(updateOutputPrice)

  useAtom(updateCurrencyBalanceParams)
  useAtom(updateBalancesStorage)
  useAtom(updateNativeBalance)
  useAtom(updateCurrentInputBalance)

  useAtom(updateCurrencyAllowanceParams)
  useAtom(updateAllowance)
  useAtom(updateAllowanceTakerAddress)

  useAtom(updateEscrowStatus)
  useAtom(updateIndexAnatomy)

  useAtom(updateUserContractStatus)

  useAtom(updateRebalanceStatus)

  useAtom(updateApproveResponse)
  useAtom(updatePermitResponse)
  useAtom(updateDepositResponse)
  useAtom(forceAllowanceUpdate)

  useAtom(actionInputCurrencyChange)
  useAtom(actionOutputCurrencyChange)
  useAtom(updateRetryAmount)

  useAtom(replacePendingHash)

  return storage
}
